// Contracts between the game client (client/game.ts) and the visual lanes.
// The game client owns netcode and state; each view only draws what it's handed.
// Views must be allocation-free per frame (reuse objects) and must never touch the sim.

import type * as THREE from 'three';
import type { World } from '../../sim/world';

export interface ViewCtx {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  world: World;
  /** graphics preset 0 low .. 3 ultra */
  quality: number;
}

// ------------------------------------------------------------------ robots
export interface EnemyRS {
  id: number;
  type: number; // EType
  x: number; y: number; z: number; yaw: number;
  /** EState: 0 chase 1 windup 2 attack 3 recover 4 stun 5 spawn */
  st: number;
  /** 0..1 progress through the current state (drives telegraphs) */
  prog: number;
  hp: number; // 0..1
  shield: number; // 0..1
  flags: number; // EFlag bits: 1 primed 2 hyper 4 stun 8 burn 16 vuln 32 strip 64 airborne
  /** seconds since last hit (hit flash), large if never */
  hitAge: number;
  /** horizontal speed m/s, for gait */
  speed: number;
  /** what the tiny eyes look at (nearest cowboy) */
  lookX: number; lookY: number; lookZ: number;
  /** telegraph aim point during windup (sniper laser end, bomber landing) */
  aimX: number; aimY: number; aimZ: number; hasAim: boolean;
  /** shooting feel (client/feel): world direction the last hit pushed it (unit, xz) and how hard
   *  relative to its hull (0 graze .. 1 a quarter of its hull .. 2 huge) */
  hitDX?: number; hitDZ?: number; hitPow?: number;
  /** the last hit glanced off armour: the hit flash stays dull (it isn't hurting much) */
  hitDull?: boolean;
  /** EFlag2 bits: 1 slowed (ice / a Time grenade), 2 staggered (WEIGHT.md) */
  flags2?: number;
  /** ROSTER.md (sim/protocol.ts EExt): affix bits 0-2, cloaked 8, big 16, routing 32, redshell 64 */
  ext?: number;
  /** the parts shot off (sim/defs.ts Part bits: top 1, right arm 2, left arm 4, legs 8), playtest 3 */
  parts?: number;
}

/** How a robot dies on screen (client/feel picks it from the killing blow). */
export const enum DeathKind { Topple = 0, Blown = 1, Headshot = 2, Shatter = 3 }

export interface EnemyViewApi {
  /** called every render frame with the visible robots (pooled array, read only) */
  sync(list: readonly EnemyRS[], count: number, dt: number, time: number): void;
  /** the killing blow, just before the robot leaves the list: kind, world push dir (xz), power */
  kill?(id: number, kind: DeathKind, dx: number, dz: number, pow: number): void;
  dispose?(): void;
}

// ------------------------------------------------------------------ cowboys
export interface CowboyRS {
  id: number;
  slot: number; // 0..3, drives scarf colour (SLOT_COLORS)
  cls: number; // ClassId
  isLocal: boolean;
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  yaw: number; pitch: number;
  /** Move enum: 0 ground 1 air 2 hover 3 flight(traversal, guns holstered) 4 dodge 5 special */
  move: number;
  grounded: boolean;
  phase: number; // 0 alive 1 down 2 dead
  skyT: number; // 0..1 pause lift (already applied to y)
  onFire: boolean;
  /** caught in a Time grenade: the wiggle blur (cowboys/index.ts) */
  slowed?: boolean;
  /** the last hit landed on the shield: the shell pulses out from the impact (cowboys/index.ts) */
  hurtShield?: boolean;
  /** the elements on a cowboy (sim/elements.ts): acid-softened (a green sheen), wet */
  acid?: boolean; wet?: boolean;
  cloaked: boolean;
  reloading: boolean;
  /** seconds since the last shot (recoil, muzzle) */
  shotAge: number;
  /** seconds since melee started, large if none */
  meleeAge: number;
  /** first-person hides own body: false in this third-person game, kept for later */
  hidden: boolean;
  name: string;
  // ---- movement detail (Player movement lane; all optional so labs and older callers still work)
  /** aiming down sights (L2) */
  ads?: boolean;
  /** sprinting (L3) */
  sprint?: boolean;
  /** DodgeK of the current / last dodge: 0 roll (or a slide, see `slide`), 1 air dive, 2 barrel roll, 3 hover dodge */
  dodgeK?: number;
  /** the roll in progress is a slide out of a sprint (movement lane) */
  slide?: boolean;
  /** a barrel roll is in progress (traversal flight keeps move = 3 through it) */
  barrel?: boolean;
  /** the barrel roll's juke side seen from behind: 1 right, -1 left */
  barrelSide?: number;
  /** LandK: 0 none, 1 soft, 2 superhero three-point landing, 3 prone after an air dive (belly slide, then the get-up) */
  landK?: number;
  /** the hover stall is braking (nose up, guns coming out) */
  stall?: boolean;
  /** scripted pose (Saloon lane): 1 bar sit, 2 bar eat, 3 superhero land, 4 launch brace; scriptedT seconds in */
  scripted?: number;
  scriptedT?: number;
  /** Saloon lane: the right hand reaches for a world point (the hotdog: plate, held, to the mouth), 0..1 */
  reachW?: number;
  reachX?: number; reachY?: number; reachZ?: number;
  /** Saloon lane: the right hand's fingers close (holding something), 0..1 */
  fist?: number;
  /** Saloon lane: the head dips to meet the hand (a bite), 0..1 */
  headDip?: number;
  /** weapons lane: the weapon in hand (sim/weapons.ts id; -1 = the class's baked gun) */
  weapon?: number;
  /** pistol-grip weapon in hand (pose hint for the animation lane) */
  pistol?: boolean;
  /** seconds since the last grenade throw, large if none (throw animation) */
  throwAge?: number;
  /** heal lane: seconds since this cowboy uncorked a bottle of whiskey, large if none (the chug and the toss) */
  drinkAge?: number;
  /** heal lane: how he ditches the empty (sim/whiskey.ts Ditch, rolled by the server) */
  ditch?: number;
  /** guns away (the saloon car: nobody draws on Pop) */
  holstered?: boolean;
  /** melee lane: the class energy blade is drawn (the gun is put away) */
  blade?: boolean;
  /** melee lane: the flourish that meleeAge times (0 the draw strike, 1..3 the chain) */
  bladeStep?: number;
  /** weight lane: seconds since the last hit that hurt (large if none), the world direction it shoved (unit xz)
   *  and how hard against the hull (0 graze .. 1 a big hit): the body flinches away from it */
  hurtAge?: number; hurtDX?: number; hurtDZ?: number; hurtPow?: number;
  /** weight lane: seconds since this cowboy was knocked off his feet (large if none), the KnockKind (1 floored,
   *  2 flung into a ragdoll) and the shove's world direction (unit xz) and speed */
  knockAge?: number; knockKind?: number; knockDX?: number; knockDZ?: number; knockSpeed?: number;
  /** heat 0..1 (the pack glows red toward the top; above 0.8 it hurts; onFire is the fire itself) */
  heat?: number;
  /** seconds since this cowboy warped or blinked (the Phantom: a double image behind, a yellow wrap through the i-frames) */
  warpAge?: number;
  /** the shield skin (fx/shieldskin.ts, filled by ShieldTracker): shield and overshield fractions 0..1, seconds since
   *  each broke, since the shield came back from empty, and since the Juggernaut's Charge poured it forward */
  shieldK?: number; overK?: number; shieldBrkAge?: number; overBrkAge?: number; shieldRegenAge?: number; shieldDrainAge?: number;
}

export interface CowboyViewApi {
  sync(list: readonly CowboyRS[], count: number, dt: number, time: number): void;
  /** world-space muzzle position for a cowboy (for tracers); writes into out */
  muzzle(id: number, out: THREE.Vector3): boolean;
  /** smoothed 0..1 jet throttle per cowboy (plumes and jetpack sound share it) */
  jetOf?(id: number): number;
  dispose?(): void;
}

// ------------------------------------------------------------------ world
export interface WorldViewApi {
  /** cowboys push grass; up to 4 positions */
  update(dt: number, time: number, camPos: THREE.Vector3, pushers: readonly { x: number; y: number; z: number }[]): void;
  /** wave/island state for set-dressing (warp gate charge etc.) 0..1 */
  setGateCharge?(v: number): void;
  dispose?(): void;
}

// ------------------------------------------------------------------ effects
export interface FxApi {
  muzzle(pos: THREE.Vector3, dir: THREE.Vector3, cls: number, local: boolean): void;
  tracer(from: THREE.Vector3, to: THREE.Vector3, cls: number, local: boolean): void;
  /** kind: 1 world 2 robot body 3 head 4 armor ricochet 5 shield */
  impact(pos: THREE.Vector3, normal: THREE.Vector3, kind: number): void;
  explosion(pos: THREE.Vector3, radius: number, kind: number): void;
  /** class flavour: 0 overload burst 1 element storm 2 chain fracture 3 stagger quake */
  detonation(pos: THREE.Vector3, radius: number, cls: number, chain: number): void;
  primeBurst(pos: THREE.Vector3): void;
  robotDeath(pos: THREE.Vector3, type: number): void;
  spawnWarn(pos: THREE.Vector3, type: number, seconds: number): void;
  playerHurt(pos: THREE.Vector3): void;
  dust(pos: THREE.Vector3, amount: number): void;
  jetFlame(pos: THREE.Vector3, dir: THREE.Vector3, power: number): void;
  /** number popping off a robot; flags as EnemyHit flags */
  damageNumber(pos: THREE.Vector3, dmg: number, flags: number, mine: boolean): void;
  /** enemy projectiles in flight, by id; kind = BKind */
  bullets(list: readonly { id: number; kind: number; x: number; y: number; z: number; vx: number; vy: number; vz: number; age: number }[], count: number): void;
  /** landing markers for lobbed bombs */
  bombMarker(id: number, x: number, y: number, z: number, radius: number, seconds: number): void;
  clearBombMarker(id: number): void;
  update(dt: number, time: number, camera: THREE.Camera): void;
}

export interface SfxApi {
  /** spatial if pos given; name from the bank in client/audio/sfx.ts */
  play(name: string, pos?: { x: number; y: number; z: number }, opts?: { volume?: number; pitch?: number }): void;
  /** looping sounds keyed by id (jetpack, flight wind, laser charge); volume 0 stops */
  loop(key: string, name: string, volume: number, pos?: { x: number; y: number; z: number }, pitch?: number): void;
  setListener(pos: THREE.Vector3, forward: THREE.Vector3): void;
  setMusicIntensity(v: number): void;
  unlock(): void;
  /** Pop's mumble voice for a line of dialogue; returns the line's seconds */
  say?(text: string, tag?: string): number;
  /** a scored moment on the frame ('sendoff': the roof-launch score) */
  cue?(name: 'sendoff'): void;
}

// ------------------------------------------------------------------ HUD
export interface HudTeammate {
  id: number; name: string; slot: number; cls: number;
  hp: number; hpMax: number; shield: number; shieldMax: number; heat: number; heatMax: number;
  phase: number; paused: boolean; connected: boolean; reviveFrac: number;
  /** projected screen position (css px) of their head; onScreen false → use angle for an edge arrow */
  sx: number; sy: number; onScreen: boolean; angle: number; dist: number;
}
export interface HudMarker {
  /** 'spawn' robots arriving, 'bomb' landing zone, 'downed' teammate down, 'primed' primed robot */
  kind: 'spawn' | 'bomb' | 'downed' | 'primed' | 'gate' | 'ping' | 'bell' | 'train' | 'bonus' | 'powerup' | 'crate';
  sx: number; sy: number; onScreen: boolean; angle: number; dist: number; t: number;
  /** 'ping' only: who pinged (slot colour); label is the ping's tag, or the waypoint caption override (gate: the next island's name) */
  slot?: number;
  label?: string;
}
/** the pickup card: what a drop is, what it does, and what taking it needs */
export interface HudPickup {
  kind: 'crate' | 'powerup';
  /** 'near' = in reach of the card; 'got' = you just took it */
  mode: 'near' | 'got';
  title: string; tag: string; blurb: string;
  /** the item's accent, css */
  color: string;
  /** the button glyph for the action row ('' none), its emphasised verb and the quieter rest */
  glyph: string; verb: string; rest: string;
  dist: number;
  /** seconds since it opened on this item; fade 1..0 (the ACQUIRED card's tail, walking away) */
  age: number; fade: number;
}
/** the one line that always says what to do next (top centre, under the work order) */
export interface HudPowerUp {
  name: string; color: string; name2: string;
  active: boolean; activeName: string; activeColor: string; frac: number;
  /** tried to fire a timed one while another runs */
  denied: boolean;
}
export interface HudObjective {
  title: string; detail: string;
  /** seconds left, < 0 = no clock */
  timer: number;
  /** 0 calm phosphor, 1 amber (go now), 2 red (danger) */
  tone: number;
}
/** One lamp under the boss bar (a Cerberus head...): ring = pool, gold ring = open weak point, X = broken. */
export interface HudBossPip { label: string; frac: number; broken: boolean; open: boolean }
/** The ONE boss bar (BOSS_GRAMMAR G8). Each boss's client module (client/render/run/bosses) fills it. */
export interface HudBoss {
  name: string; sub: string;
  hp: number; // 0..1
  /** lamps along the left under the bar (0..4) */
  pips: HudBossPip[];
  /** right of the name plate in a quieter voice: a body count ('27 OF 36'), '' none */
  count: string;
  /** hyper shield up: the bar goes magenta and the crack meter shows under it, labelled `meter` */
  hyper: boolean; meter: string; crack: number;
  /** a blinking amber call at the right under the bar ('' none): FURNACE CORE EXPOSED, DRILL CALL: GRINDER */
  note: string;
  /** big caution under the bar ('' none): TAKE COVER on the howl tell */
  warn: string;
  /** what the reticle is on ('' none): JAW, CORE, HEAD, PLATE; amber when it contains CRIT */
  aimPart: string;
}
/** A big centre-screen call (chains, team plays, multikills, achievements). Age in seconds. */
export interface HudCallout { text: string; sub: string; tone: 'prime' | 'amber' | 'phos' | 'red'; age: number }
/** The per-gun sight. Angles are given as a fraction of half the screen height (tan(a) / tan(fov/2)),
 *  so a ring drawn at spread x h/2 covers exactly where the rounds can land. */
export interface HudSight {
  gun: number; // weapon id (W)
  ads: boolean;
  spread: number; // the spread cone's radius, right now (aim, movement, spin and fan bloom included)
  track: number; // the tracking cone's radius (0 = not a tracking gun)
  lock: boolean; lockX: number; lockY: number; // the robot a tracked round would bend to, on screen (px)
  spin: number; // Auto MG spin 0..1
  steady: number; // sniper dead-eye 0..1
  bloom: number; // Big Iron fan bloom 0..~3
  burstHits: number; burstRound: number; // Pulse Rifle: hits / rounds fired in the burst so far
  overloadAge: number; // seconds since your last overload pulse
  charge: number; // Duel Blaster 0..1 (-1 not charging)
  chambers: number; chamberMax: number;
}

export interface HudClassCue {
  detReady: boolean; detVerb: string; detHint: string;
  primeReady: boolean; primeHint: string;
  extender: string; cloakSecs: number;
}

export interface HudState {
  w: number; h: number; dpr: number; time: number;
  glyphs: 'kbm' | 'pad';
  // you
  cls: number; className: string; weaponName: string; slot: number; name: string;
  hp: number; hpMax: number; shield: number; shieldMax: number; heat: number; heatMax: number;
  onFire: boolean; heatDeniedAge: number; shieldBrokeAge: number;
  /** the elements on you (sim/elements.ts): burning (through the shield), acid-softened, wet */
  burning?: boolean; acid?: boolean; wet?: boolean;
  mag: number; magMax: number; reloadFrac: number; // -1 not reloading
  /** weapons lane: grenade belt, the other gun, the special, blaster charge (0..1, -1 none) */
  grenadeName?: string; grenades?: number; grenadeMax?: number; grenadeBank?: number;
  otherWeaponName?: string; specialName?: string; specialAmmo?: number; specialBank?: number; slotIndex?: number; chargeFrac?: number;
  /** run lane: the held power-up ('' none), the second slot's, and the running buff (frac 1→0) */
  powerup?: HudPowerUp;
  /** wildcard cards (sim/cards.ts): the hand's names, in order taken */
  cards?: string[];
  /** heal lane: bottles of whiskey in the belt, the heal running (1..0) and the chug (1..0); low = hurt enough to
   *  want one; empty = just reached for an empty belt */
  whiskey?: { n: number; max: number; heal: number; chug: number; low: boolean; empty: boolean };
  /** a fused grenade cooking in the hand, 0..1 of its fuse (-1 none) */
  cookFrac?: number;
  /** weapons lane: the gun's own sight (GUN_IDENTITIES.md) */
  sight?: HudSight;
  move: number; grounded: boolean; phase: number; bleedOut: number; reviveFrac: number;
  paused: boolean; skyT: number;
  chainFrac: number; chainN: number; cloaked: boolean;
  primerVerb: string; detonatorVerb: string; abilityName: string; abilityFrac: number; // 1 = ready
  /** class lane (sim/defs class verbs): for the robot under the reticle, whether the class detonator would land
   *  right now (detVerb says why, detHint what to do if not), whether the class primer would; the chain window's
   *  loop extender ('' none); seconds of cloak left */
  classCue?: HudClassCue;
  // what the reticle is over: -1 nothing, else EType; aimPrimed true if it's primed
  aimType: number; aimPrimed: boolean; aimHyper: boolean; aimHp: number; aimShield: number; aimDist: number;
  hitAge: number; killAge: number; headAge: number; detonateAge: number;
  /** shooting feel (client/feel): what the last hit was (0 body 1 crit 2 armour, no damage 3 shield),
   *  how hard (0..1+), and the kill confirm: headshot, a heavy robot, multikill count in the streak */
  hitKind?: number; hitPow?: number; killHead?: boolean; killBig?: boolean; killN?: number;
  hurtDirs: { angle: number; age: number }[];
  // posse + run
  posse: HudTeammate[];
  markers: HudMarker[];
  lives: number; wave: number; waveState: number; tier: number; dirTimer: number; enemiesLeft: number;
  notices: { text: string; age: number }[];
  frozen: boolean;
  objective?: HudObjective | null;
  boss?: HudBoss | null;
  /** 0..1 run-wide colour drain (extraction greyscale); the visor stays lit */
  extract?: number;
  /** posse panel held/toggled open (Tab / Create): every teammate tab expands */
  posseOpen?: boolean;
  /** centre calls, newest last (client/review/feedback.ts) */
  callouts?: HudCallout[];
  /** quick-chat wheel: open, which line the stick points at (-1 none = ping) and the four lines */
  wheel?: { open: boolean; sel: number; lines: readonly string[] };
  /** a one-time teach line from Pop on the radio (client/review/coach.ts), with its glyph */
  coach?: { text: string; glyph: string; age: number } | null;
  /** the pickup card (client/hud/pickup.ts): the crate or power-up you're next to, or the one you just took */
  pickup?: HudPickup | null;
  // debug
  showNet: boolean;
  net: { ping: number; jitter: number; loss: number; cushion: number; corrections: number; interp: number; kbpsIn: number; kbpsOut: number; fps: number; frameMs: number; draws: number; tris: number; tickRate: number };
}

export interface HudApi {
  /** the canvas texture the post pass composites; premultiplied alpha */
  readonly texture: THREE.Texture;
  /** draw; returns true if the texture changed */
  render(s: HudState): boolean;
  resize(w: number, h: number, dpr: number): void;
}
